import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { redlistOptions } from './CreateProjectModal.tsx'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import type { Taxas } from 'Model/ITaxa.ts'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.ts'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../../../Generated/ProcessingType.ts'

export interface EditProjectModalProps {
  project: IEvaluationProject
}

export const EditProjectModal = ({ isOpen, onClose, project }: EditProjectModalProps & BaseModalProps) => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [targetTaxonomy, setTargetTaxonomy] = useState<Taxas | null>(null)
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey | null>(null)
  const [processingType, setProcessingType] = useState<ProcessingTypeKey | null>(null)

  // Initialize form with project data when the modal opens
  useEffect(() => {
    if (isOpen && project) {
      setName(project.name)
      setDescription(project.description)
      setTargetTaxonomy(project.targetTaxonomy || null)
      setRedlistMode(project.redlistMode || null)
      setProcessingType(project.processingType || null)
    }
  }, [isOpen, project])

  const handleSubmit = () => {
    // Form validation
    if (!name.trim()) {
      console.log('no name')
      // Name is required
      return
    }

    dispatch(
      Actions[projectSlice.name].updateProjectRequest({
        ...project,
        name,
        description,
        targetTaxonomy,
        redlistMode,
        // grouping is not a user-facing setting (no control anywhere); round-trip it so a save can't wipe
        // a value that was set through the API
        groupingMode: project.groupingMode,
        processingType,
      })
    )
    onClose()
  }

  const resetForm = () => {
    setName(project.name)
    setDescription(project.description)
    setTargetTaxonomy(project.targetTaxonomy || null)
    setRedlistMode(project.redlistMode || null)
    setProcessingType(project.processingType || null)
  }

  return (
    <BaseModal onClose={onClose} isOpen={isOpen}>
      <div className="modal-box">
        <h3 className="text-lg font-bold">Edit Project</h3>
        <div className="form-control w-full">
          <label className="label">
            <span className="label-text">Project Name</span>
          </label>
          <input
            data-testid={'project-name'}
            type="text"
            placeholder="Enter project name"
            className="input input-bordered w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            placeholder="Enter project description"
            className="textarea textarea-bordered w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">
              Target Taxonomy
              <br />
              <span className={'text-xs'}>(Optional, used to determine default evaluation settings)</span>
            </span>
          </label>
          <select
            id="taxa"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={targetTaxonomy || ''}
            onChange={(e) => setTargetTaxonomy((e.target.value as Taxas) || null)}
          >
            {[{ value: undefined, label: 'None', disabled: false }, ...taxaOptions].map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Redlist Mode</span>
          </label>
          <select
            id="redlistMode"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={redlistMode || ''}
            onChange={(e) => setRedlistMode((e.target.value as RedlistModeKey) || null)}
          >
            <option value="">Organization Default</option>
            {redlistOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-control w-full mt-4">
          <label className="label">
            <span className="label-text">Processing Type</span>
          </label>
          <select
            id="processingType"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={processingType || ''}
            onChange={(e) => setProcessingType((e.target.value as ProcessingTypeKey) || null)}
          >
            <option value="">Any</option>
            {Object.values(ProcessingTypes).map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-action">
          <button
            className="btn btn-ghost mr-2"
            onClick={() => {
              resetForm()
              onClose()
            }}
          >
            Cancel
          </button>
          <button data-testid="save-project" className={`btn btn-primary`} onClick={handleSubmit} disabled={!name.trim()}>
            Save Changes
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
